import { defineStore } from 'pinia'
import { invoke } from '~/lib/tauriInvoke'
import { runAction } from '~/stores/storeErrorHandling'
import { useUserStore } from '~/stores/user'
import type { CheckoutOutcome } from '~/lib/subscriptions/yookassaWidget'
import type {
  AiModels,
  AiUsage,
  AiUsageQuery,
  CreateSubscriptionPaymentResponse,
  GetSubscriptionPlansResponse,
  MyCredits,
  MySubscription,
  NotificationSettings,
  SubscriptionPaymentStatusResponse,
  SubscriptionPlan,
} from '~/types/subscriptions'

export type {
  AiModel, AiModels, AiUsage, AiUsageByAgent, AiUsageByModel, AiUsageDay, AiUsageItem, AiUsageQuery, AiUsageTotals,
  DemoOffer, MyCredits, MySubscription, NotificationSettings, SubscriptionPlan,
} from '~/types/subscriptions'

/** How often, and for how long, a payment is polled after the widget closes (see proto comment). */
const PAYMENT_POLL_INTERVAL_MS = 2_000
const PAYMENT_POLL_TIMEOUT_MS = 3 * 60_000

export type CheckoutState =
  | { phase: 'idle' }
  | { phase: 'creating'; planId: string }
  /** The checkout dialog (SubscriptionCheckoutDialog.vue) is showing the widget for this token. */
  | { phase: 'widget'; planId: string; paymentId: string; confirmationToken: string }
  | { phase: 'verifying'; planId: string; paymentId: string }
  | { phase: 'succeeded'; planId: string }
  | { phase: 'canceled'; planId: string; reason: string | null }
  /** Still pending when polling gave up - the backend may yet confirm it (push / next open). */
  | { phase: 'pending'; planId: string }
  | { phase: 'error'; planId: string; message: string }

function isFinalStatus(status: string) {
  return status === 'Succeeded' || status === 'Canceled'
}

/** IANA zone of the device ("Europe/Moscow"); empty lets the server fall back to UTC. */
export function localTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? ''
  } catch {
    return ''
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/** Settles the `purchase` waiting on the checkout dialog - see `finishCheckoutWidget`. */
let settleWidget: ((outcome: CheckoutOutcome | Error) => void) | null = null

export const useSubscriptionsStore = defineStore('subscriptions', {
  state: () => ({
    plans: [] as SubscriptionPlan[],
    mySubscription: null as MySubscription | null,
    myCredits: null as MyCredits | null,
    isLoadingCredits: false,
    /** Platform-key models and prices — a catalog, so loaded once per session. */
    aiModels: null as AiModels | null,
    isLoadingAiModels: false,
    plansLoaded: false,
    isLoadingPlans: false,
    isLoadingMine: false,
    isUpdatingAutoRenew: false,
    isActivatingDemo: false,
    /** Server-side alert preferences; null until first loaded. */
    notificationSettings: null as NotificationSettings | null,
    isLoadingNotificationSettings: false,
    isUpdatingNotificationSettings: false,
    checkout: { phase: 'idle' } as CheckoutState,
    error: null as string | null,
  }),
  getters: {
    hasActiveSubscription: (state) => !!state.mySubscription?.is_active,
    /** Set only while the account may still take its one-time demo. */
    demoOffer: (state) => state.mySubscription?.demo ?? null,
    isCheckoutBusy: (state) =>
      state.checkout.phase === 'creating' || state.checkout.phase === 'widget' || state.checkout.phase === 'verifying',
  },
  actions: {
    /** Plans rarely change - fetched once per session unless `force`d. */
    async fetchPlans(force = false) {
      if (this.plansLoaded && !force) return
      this.isLoadingPlans = true
      try {
        await runAction(
          (msg) => (this.error = msg),
          { action: 'fetchSubscriptionPlans', rethrow: false },
          async () => {
            const response = await invoke<GetSubscriptionPlansResponse>('get_subscription_plans')
            this.plans = response.plans
            this.plansLoaded = true
          },
        )
      } finally {
        this.isLoadingPlans = false
      }
    },

    async fetchMySubscription() {
      this.isLoadingMine = true
      try {
        await runAction(
          (msg) => (this.error = msg),
          { action: 'fetchMySubscription', rethrow: false },
          async () => this.applyMySubscription(await invoke<MySubscription>('get_my_subscription')),
        )
      } finally {
        this.isLoadingMine = false
      }
    },

    /** AI-agent credit balance - re-read on every call: spending happens server-side with no push. */
    async fetchMyCredits() {
      this.isLoadingCredits = true
      try {
        await runAction(
          (msg) => (this.error = msg),
          { action: 'fetchMyCredits', rethrow: false },
          async () => {
            this.myCredits = await invoke<MyCredits>('get_my_credits')
          },
        )
      } finally {
        this.isLoadingCredits = false
      }
    },

    async fetchAiModels() {
      if (this.aiModels || this.isLoadingAiModels) return
      this.isLoadingAiModels = true
      try {
        await runAction(
          (msg) => (this.error = msg),
          { action: 'fetchAiModels', rethrow: false },
          async () => {
            this.aiModels = await invoke<AiModels>('get_ai_models')
          },
        )
      } finally {
        this.isLoadingAiModels = false
      }
    },

    /**
     * Agents' token usage — not kept in the store: it is per agent/period and changes with every
     * request, so callers re-read it when shown. Throws on failure. The per-day summary is cut in
     * the device's zone unless `timeZone` says otherwise.
     */
    async fetchAiUsage(query: AiUsageQuery = {}): Promise<AiUsage> {
      const args = {
        limit: query.limit ?? 20,
        agentId: query.agentId ?? null,
        from: query.from ?? null,
        to: query.to ?? null,
        timeZone: query.timeZone ?? localTimeZone(),
        cursor: query.cursor ?? null,
      }
      return (await runAction(
        (msg) => (this.error = msg),
        { action: 'fetchAiUsage', extra: { agentId: args.agentId } },
        () => invoke<AiUsage>('get_ai_usage', args),
      )) as AiUsage
    },

    async fetchNotificationSettings() {
      this.isLoadingNotificationSettings = true
      try {
        await runAction(
          (msg) => (this.error = msg),
          { action: 'fetchNotificationSettings', rethrow: false },
          async () => {
            this.notificationSettings = await invoke<NotificationSettings>('get_notification_settings')
          },
        )
      } finally {
        this.isLoadingNotificationSettings = false
      }
    },

    /** Optimistic: the switch flips immediately and rolls back if the server refuses. Throws on failure. */
    async setCreditAlertsEnabled(enabled: boolean) {
      const previous = this.notificationSettings
      this.notificationSettings = { ...(previous ?? {}), credit_alerts_enabled: enabled }
      this.isUpdatingNotificationSettings = true
      try {
        this.notificationSettings = (await runAction(
          (msg) => (this.error = msg),
          { action: 'setNotificationSettings', extra: { enabled } },
          () => invoke<NotificationSettings>('set_notification_settings', { creditAlertsEnabled: enabled }),
        )) as NotificationSettings
      } catch (e) {
        this.notificationSettings = previous
        throw e
      } finally {
        this.isUpdatingNotificationSettings = false
      }
    },

    async setAutoRenew(autoRenew: boolean) {
      this.isUpdatingAutoRenew = true
      try {
        return await runAction(
          (msg) => (this.error = msg),
          { action: 'setSubscriptionAutoRenew', extra: { autoRenew } },
          async () => this.applyMySubscription(await invoke<MySubscription>('set_subscription_auto_renew', { autoRenew })),
        )
      } finally {
        this.isUpdatingAutoRenew = false
      }
    },

    /**
     * One-time Premium demo: the backend switches the tier on for `demo.days` with its credits, no
     * payment involved. Throws on refusal (already used, active subscription) with the server's text.
     */
    async activateDemo() {
      if (this.isActivatingDemo) return
      this.isActivatingDemo = true
      try {
        const subscription = (await runAction(
          (msg) => (this.error = msg),
          { action: 'activateDemo' },
          () => invoke<MySubscription>('activate_demo'),
        )) as MySubscription
        this.applyMySubscription(subscription)
        void this.fetchMyCredits()
        return subscription
      } catch (e) {
        // Refused because it's no longer offered: re-read so the offer disappears from the page.
        void this.fetchMySubscription()
        throw e
      } finally {
        this.isActivatingDemo = false
      }
    },

    /**
     * Full purchase flow: create the payment, hand its token to the YooKassa widget, then poll
     * the backend until the payment settles. Progress is exposed through `checkout`; the promise
     * itself never rejects. `email` is where YooKassa sends the receipt.
     */
    async purchase(planId: string, email: string, autoRenew: boolean) {
      if (this.isCheckoutBusy) return
      this.checkout = { phase: 'creating', planId }
      try {
        const payment = await invoke<CreateSubscriptionPaymentResponse>('create_subscription_payment', {
          payload: { plan_id: planId, email, auto_renew: autoRenew },
        })

        if (payment.confirmation_token && !isFinalStatus(payment.status)) {
          const confirmationToken = payment.confirmation_token
          this.checkout = { phase: 'widget', planId, paymentId: payment.payment_id, confirmationToken }
          // The checkout dialog renders the widget and reports back through `finishCheckoutWidget`.
          await new Promise<CheckoutOutcome>((resolve, reject) => {
            settleWidget = (outcome) => (outcome instanceof Error ? reject(outcome) : resolve(outcome))
          })
        }

        // Even a closed-without-finishing widget gets one check: the user may have paid via an
        // external app (SBP/SberPay) and closed the modal before it noticed.
        this.checkout = { phase: 'verifying', planId, paymentId: payment.payment_id }
        await this.pollPayment(planId, payment.payment_id)
      } catch (e: any) {
        const message = e?.message || String(e)
        console.error('[subscriptions] purchase failed:', e)
        this.checkout = { phase: 'error', planId, message }
      }
    },

    /** Called by the checkout dialog once the widget is done with: paid, confirmed close, or error. */
    finishCheckoutWidget(outcome: CheckoutOutcome | Error) {
      const settle = settleWidget
      settleWidget = null
      settle?.(outcome)
    },

    async pollPayment(planId: string, paymentId: string) {
      const deadline = Date.now() + PAYMENT_POLL_TIMEOUT_MS
      while (Date.now() < deadline) {
        // A newer checkout (or `resetCheckout`) took over - this loop is stale.
        if (this.checkout.phase !== 'verifying' || this.checkout.paymentId !== paymentId) return
        const status = await invoke<SubscriptionPaymentStatusResponse>('get_subscription_payment', { paymentId })
        if (status.status === 'Succeeded') {
          if (status.subscription) this.applyMySubscription(status.subscription)
          else await this.fetchMySubscription()
          this.checkout = { phase: 'succeeded', planId }
          return
        }
        if (status.status === 'Canceled') {
          this.checkout = { phase: 'canceled', planId, reason: status.cancellation_reason ?? null }
          return
        }
        await sleep(PAYMENT_POLL_INTERVAL_MS)
      }
      if (this.checkout.phase === 'verifying' && this.checkout.paymentId === paymentId) {
        this.checkout = { phase: 'pending', planId }
      }
    },

    resetCheckout() {
      if (this.isCheckoutBusy && this.checkout.phase !== 'verifying') return
      this.checkout = { phase: 'idle' }
    },

    /** `UserSubscriptionUpdated` on /hubs/users: the backend changed our subscription (payment, renewal, expiry). */
    async handleRealtimeUpdate() {
      await this.fetchMySubscription()
      if (this.checkout.phase === 'pending' && this.mySubscription?.is_active) {
        this.checkout = { phase: 'succeeded', planId: this.checkout.planId }
      }
    },

    applyMySubscription(subscription: MySubscription) {
      this.mySubscription = subscription
      // Keep the `User.subscription` summary (common.Subscription) in step, so anything gating
      // features on the current user's tier sees the change without a `get_me` round-trip.
      const userStore = useUserStore()
      if (userStore.user) {
        userStore.user.subscription = subscription.is_active
          ? {
              id: subscription.id ?? '',
              tier_code: subscription.tier_code,
              functions: subscription.functions,
              activated_at: subscription.activated_at ?? null,
              expires_at: subscription.expires_at ?? null,
            }
          : null
      }
      return subscription
    },

    clear() {
      this.$reset()
    },
  },
})
